import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ContentBlock } from '../ContentBlocks';
import { LAYOUT_WIDTH, LAYOUT_HEIGHT } from '../utils/customHtmlRaster';
import {
  EXPORT_HEADER_H,
  EXPORT_SLIDE_H,
  frameForBlock
} from '../utils/slideFrames';

/**
 * The header bar's share of the slide, taken from the export geometry rather
 * than guessed.
 *
 * This was hard-coded at 14%, which left the body box shorter than the stage it
 * had to hold: the stage is the content region (LAYOUT_HEIGHT), so a body box
 * of the wrong height meant the scale-to-width put the last few percent of
 * every slide below the fold, clipped. 0.85in of 7.5in is 11.33%, and with that
 * the two boxes agree and the miniature ends where the slide ends.
 */
export const HEADER_BAR_PERCENT = (EXPORT_HEADER_H / EXPORT_SLIDE_H) * 100;

/**
 * SlideLiveThumbnail
 * ------------------
 * A drawer thumbnail that is the slide, not a sketch of it.
 *
 * `SlideThumbnailPreview` approximates each block with a hand-written miniature
 * — real text, but invented layout. This renders the *actual* block components
 * in their read-only form onto a slide-sized stage and scales the whole stage
 * down with a transform, so what the drawer shows is what the slide shows.
 *
 * The technique is lifted from `CustomHtmlMini` inside SlideThumbnailPreview,
 * which has done exactly this for hand-written layouts for a while. The reason
 * it works is that laying out at slide size and scaling afterwards is the only
 * way a fixed-size design survives miniaturisation: reflowing the same content
 * into a 150px-wide box would collapse every grid and px measurement into a
 * smear. It is also what the PowerPoint and PDF rasterisers do, so the drawer,
 * the exports and the slide itself all agree.
 *
 * Three things keep it affordable:
 *
 *   * Read-only. `isEditMode={false}` is the same prop SlideDesigner passes for
 *     its own previews (LessonTemplate.js), so no editors, drag handles or
 *     block toolbars mount.
 *   * Inert. The stage takes no pointer events and is hidden from the
 *     accessibility tree; the drawer row's own overlay button owns the click.
 *   * Deferred. The stage only mounts once the thumbnail is near the viewport.
 *     A 28-slide deck would otherwise mount 28 slides' worth of iframes,
 *     canvases and video players the moment the drawer opened.
 */

/**
 * How far outside the viewport a thumbnail starts rendering. One drawer-height
 * of runway, so a scroll lands on content that is already painted rather than
 * on a row that pops in under the cursor.
 */
const PRERENDER_MARGIN = '600px';

/**
 * Blocks with nothing to show in a miniature.
 *
 * A divider is a hairline that vanishes at thumbnail scale, and dropping it
 * avoids a stage that is technically non-empty but visually blank.
 */
const INVISIBLE_TYPES = new Set(['divider']);

const NOOP = () => {};
const EMPTY_HTML_MODES = {};

/**
 * True once the element has been near the viewport, and sticky thereafter.
 *
 * Sticky on purpose: a thumbnail that unmounted when scrolled away would throw
 * out its layout and re-run every image decode on the way back, which is more
 * expensive than keeping the DOM around.
 */
const useHasBeenNearViewport = (ref) => {
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    if (seen) return undefined;
    const node = ref.current;
    if (!node) return undefined;
    // No IntersectionObserver (jsdom, older engines) means no deferral rather
    // than no thumbnail — correctness first, the cost is only ever startup.
    if (typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setSeen(true);
      },
      { rootMargin: PRERENDER_MARGIN }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, seen]);

  return seen;
};

/**
 * The scale that fits a LAYOUT_WIDTH-wide stage into the host box.
 *
 * Width alone, not `min(width, height)`: the host already carries the content
 * region's aspect ratio, so matching width matches height too — and a slide
 * whose blocks overrun the region should show the same clipped overflow the
 * real slide does, not shrink until everything fits.
 *
 * **The host is tracked as state, not through a ref**, and that is the whole
 * point of the hook rather than an inline effect. The host element only exists
 * while the slide has something to draw: an empty slide renders the "Empty
 * slide" placeholder instead, and no host at all. A `useLayoutEffect` keyed on
 * a ref object runs exactly once — the ref's identity never changes — so on a
 * slide that was empty when the drawer first painted it, `hostRef.current` was
 * `null` at that one opportunity and the effect returned having measured
 * nothing. Adding the first block then mounted the host with no effect left to
 * notice it: `scale` stayed `null`, the stage rendered at `scale(0)`, and the
 * thumbnail sat blank for the rest of the session however much content went
 * onto the slide. Reported as "thumbnails don't update when I add Custom
 * HTML/CSS", and it was never about Custom HTML — that block is simply the one
 * people add to a brand-new blank slide.
 *
 * A callback ref puts the node in state, so mounting or unmounting the host
 * re-runs the measurement, and the ResizeObserver is attached to whatever host
 * is currently on screen.
 */
const useStageScale = () => {
  const [host, setHost] = useState(null);
  const [scale, setScale] = useState(null);

  useLayoutEffect(() => {
    if (!host) return undefined;
    const measure = () => {
      const { clientWidth: width } = host;
      if (width > 0) setScale(width / LAYOUT_WIDTH);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    return () => observer.disconnect();
  }, [host]);

  return [scale, setHost];
};

const MiniBlock = ({ block }) => (
  <ContentBlock
    block={block}
    isEditMode={false}
    htmlModes={EMPTY_HTML_MODES}
    toggleHtmlMode={NOOP}
    onBlockUpdate={NOOP}
  />
);

/**
 * A canvas-mode slide, positioned the way SlideDesigner positions it.
 *
 * Canvas decks put every block at an explicit {x, y, w, h} in percent of the
 * content region, which is how a slide gets a picture beside a list rather than
 * above it. Rendering those blocks as a plain document stack — which is what
 * this component did at first — throws the entire design away and lays the
 * slide out in source order, so a two-column slide came back as one column and
 * the thumbnail looked nothing like the slide.
 *
 * The percentages are resolved against the stage, which *is* the content
 * region, so no conversion is needed: the same numbers that drive the designer
 * and the PowerPoint export drive this.
 */
const CanvasStage = ({ blocks }) => (
  <>
    {blocks.map((block, index) => {
      const frame = frameForBlock(block, index);
      return (
        <span
          key={block.id}
          className="slt__item"
          style={{
            left: `${frame.x}%`,
            top: `${frame.y}%`,
            width: `${frame.w}%`,
            height: `${frame.h}%`,
            // Source order is stacking order, matching the designer.
            zIndex: 10 + index,
            '--text-scale': frame.s || 1
          }}
        >
          <span className="slt__item-content">
            <MiniBlock block={block} />
          </span>
        </span>
      );
    })}
  </>
);

/** A flow-mode slide: blocks in document order, padded like SectionBlocks. */
const FlowStage = ({ blocks }) => (
  <span className="slt__flow">
    {blocks.map((block) => (
      <MiniBlock key={block.id} block={block} />
    ))}
  </span>
);

const SlideLiveThumbnail = ({
  blocks = [],
  headerColor = '#545f7d',
  bodyStyle,
  className = '',
  designMode = 'flow'
}) => {
  const rootRef = useRef(null);
  const [scale, setHost] = useStageScale();
  const ready = useHasBeenNearViewport(rootRef);

  const visible = useMemo(
    () => blocks.filter((block) => block && !INVISIBLE_TYPES.has(block.type)),
    [blocks]
  );

  // Trust the frames, not just the deck-level mode: a deck can be switched to
  // flow while its blocks still carry the positions they were designed at, and
  // a block with a frame is a block someone placed on purpose.
  const isCanvas = designMode === 'canvas' && visible.some((block) => block.frame);

  return (
    <span
      ref={rootRef}
      className={`slt drawer-slide-thumb--large ${className}`.trim()}
      aria-hidden="true"
    >
      <span
        className="slt__bar"
        style={{ backgroundColor: headerColor, height: `${HEADER_BAR_PERCENT}%` }}
      />
      <span className="slt__body" style={bodyStyle}>
        {visible.length === 0 ? (
          <span className="slt__empty">Empty slide</span>
        ) : (
          <span className="slt__host" ref={setHost}>
            {ready ? (
              <span
                className={`slt__stage${isCanvas ? ' slt__stage--canvas' : ''}`}
                style={{
                  width: `${LAYOUT_WIDTH}px`,
                  height: `${LAYOUT_HEIGHT}px`,
                  // Hidden until measured, so a full-size slide never flashes
                  // across the drawer on first paint.
                  transform: `scale(${scale == null ? 0 : scale})`
                }}
              >
                {isCanvas ? <CanvasStage blocks={visible} /> : <FlowStage blocks={visible} />}
              </span>
            ) : null}
          </span>
        )}
      </span>
    </span>
  );
};

export default SlideLiveThumbnail;
